import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Grid,
  Button,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

//constants
import { userAgencyOptions } from "../../Constants";

//themes
import themes from "../../themes/defaultTheme";

//Update regions list
const modifyRegionsLst = (userRegionsLst) => {
  if (userRegionsLst.length > 1) {
    // Remove "All" from the list if it exists.
    const indexOfAll = userRegionsLst.indexOf("All");
    if (indexOfAll > -1) userRegionsLst.splice(indexOfAll, 1);

    // Sort the regions alphabetically
    const sortedRegions = userRegionsLst.sort((a, b) => a.localeCompare(b));

    // Add "All" back to the top of the list
    return ["All", ...sortedRegions];
  }
  return userRegionsLst;
};

export default function UserInputDialog({
  open,
  onClose,
  onConfirm,
  firstName: initialFirstName,
  lastName: initialLastName,
  role: initialRole,
  region: initialRegion,
  agency: initialAgency,
  email: initialEmail,
  loggedInUser,
  dialogType,
}) {
  const [firstName, setFirstName] = useState(initialFirstName || "");
  const [lastName, setLastName] = useState(initialLastName || "");
  const [role, setRole] = useState(initialRole || "");
  const [region, setRegion] = useState(initialRegion || "");
  const [agency, setAgency] = useState(initialAgency || "");
  const [email, setEmail] = useState(initialEmail || "");
  const userRegionsLst = loggedInUser?.regions
    ? loggedInUser.regions.split(",")
    : [];
  const userRegionOptions = modifyRegionsLst(userRegionsLst);
  const userRoleOptions =
    loggedInUser?.roleName.toLowerCase() === "super admin"
      ? loggedInUser.allUserRoles?.split(",")
      : ["Regional User"];

  useEffect(() => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setRole(initialRole);
    setRegion(initialRegion);
    setAgency(initialAgency);
    setEmail(initialEmail);
  }, [
    initialFirstName,
    initialLastName,
    initialRole,
    initialRegion,
    initialAgency,
    initialEmail,
  ]);

  useEffect(() => {
    //Set first element as the default value if only one value is there
    if (userRegionOptions.length === 1) {
      setRegion(userRegionOptions[0]);
    }
    if (userRoleOptions.length === 1) {
      setRole(userRoleOptions[0]);
    }
  }, [open]);

  //Invoked when value in first name changes.
  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };

  //Invoked when value in last name changes.
  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };

  //Invoked when value in role changes.
  const handleRoleChange = (event, newValue) => {
    setRole(newValue || "");
  };

  //Invoked when value in region changes.
  const handleRegionChange = (event, newValue) => {
    setRegion(newValue || "");
  };

  //Invoked when value in agency changes.
  const handleAgencyChange = (event, newValue) => {
    setAgency(newValue || "");
  };

  //Invoked when value in email changes.
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleCancel = () => {
    if (dialogType === "add") {
      setFirstName("");
      setLastName("");
      setAgency("");
      setEmail("");
      setRegion("");
      setRole("");
    } else {
      setFirstName(firstName);
      setLastName(lastName);
      setRole(role);
      setRegion(region);
      setAgency(agency);
      setEmail(email);
    }
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(firstName, lastName, role, region, agency, email, () => {
      setFirstName("");
      setLastName("");
      setRole("");
      setRegion("");
      setAgency("");
      setEmail("");
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }} aria-labelledby="dialog-title">
      <DialogTitle
        id="dialog-title"
        style={{ textAlign: "center", fontWeight: "bold" }}
      >
        {dialogType === "add" ? "Add User" : "Edit User"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Info */}
          <Grid item xs={12}>
            <Typography variant="caption" style={{ fontStyle: "italic" }}>
              Fields marked with <em style={{ color: themes.default.palette.error.dark }}>*</em> are required.
            </Typography>
          </Grid>

          {/* First Name label and Textfield */}
          <Grid item xs={6}>
            <InputLabel htmlFor="first-name-text" id="first-name-label">
              First Name <em style={{ color: themes.default.palette.error.dark }}>*</em>
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="first-name"
              fullWidth
              variant="outlined"
              value={firstName}
              onChange={handleFirstNameChange}
              inputProps={{
                id: 'first-name-text',
                'aria-labelledby': 'first-name-label'
              }}
            />
          </Grid>

          {/* Last Name label and Textfield */}
          <Grid item xs={6}>
            <InputLabel htmlFor="last-name-text" id="last-name-label">Last Name</InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="last-name"
              fullWidth
              variant="outlined"
              value={lastName}
              onChange={handleLastNameChange}
              inputProps={{
                id: 'last-name-text',
                'aria-labelledby': 'last-name-label'
              }}
            />
          </Grid>

          {/* Region label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="region-autocomplete" id="region-select-label">
              Region <em style={{ color: themes.default.palette.error.dark }}>*</em>
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="region-autocomplete"
              options={userRegionOptions || []}
              value={region || ""}
              onChange={handleRegionChange}
              disabled={userRegionOptions?.length === 1}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
            />
          </Grid>

          {/* Role label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="role-autocomplete" id="role-label">
              Role <em style={{ color: themes.default.palette.error.dark }}>*</em>
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="role-autocomplete"
              options={userRoleOptions || []}
              value={role || ""}
              onChange={handleRoleChange}
              disabled={userRoleOptions?.length === 1}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
            />
          </Grid>

          {/* Email label and Textfield */}
          <Grid item xs={6}>
            <InputLabel htmlFor="email-text" id="email-label">
              Email <em style={{ color: themes.default.palette.error.dark }}>*</em>
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              inputProps={{
                id: 'email-text',
                'aria-labelledby': 'email-label'
              }}
            />
          </Grid>

          {/* Agency label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="agency-autocomplete" id="agency-select-label">
              Agency
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="agency-autocomplete"
              options={userAgencyOptions.map(option => option.label) || []}
              value={agency || ""}
              onChange={handleAgencyChange}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" onClick={handleConfirm} color="primary">
          {dialogType === "add" ? "Add" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
